/****
 * Custom authentication hook that manages user sign-in, sign-out,
 * MFA verification, and authentication state synchronization.
 *
 * Integrates with Microsoft Authentication (MSAL), synchronizes
 * authenticated user information with Redux, handles login/logout
 * flows, manages user roles, and provides a centralized interface
 * for authentication-related operations across the application.
 */
import type { AccountInfo } from "@azure/msal-browser";
import { useAccount, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useCallback, useEffect } from "react";

import { postLogoutUser } from "@/services/postLoginUser";

import { getLoginRequest } from "../auth/bootstrapMsal";
import type { Account } from "../auth/types";
import {
  selectAuthAccount,
  selectAuthRoles,
  selectAuthStatus,
  setAccount,
  setRoles,
  setStatus,
} from "../store/authSlice";
import { useAppDispatch, useAppSelector } from "../store/hooks";

/**
 * Extracts app-level Account from MSAL AccountInfo
 */
const mapMsalAccountToAccount = (msalAccount: AccountInfo): Account => {
  const claims = msalAccount.idTokenClaims as Record<string, any> | undefined;

  const displayName = claims?.name || claims?.preferred_username || msalAccount.name || "";

  const email = claims?.email || msalAccount.username || "";

  const id = msalAccount.homeAccountId?.split(".")[0] || msalAccount.localAccountId;

  return {
    id,
    username: msalAccount.username || email,
    displayName,
    email,
  };
};

export const useAuth = () => {
  const dispatch = useAppDispatch();

  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const msalAccount = useAccount();

  const status = useAppSelector(selectAuthStatus);
  const account = useAppSelector(selectAuthAccount);
  const roles = useAppSelector(selectAuthRoles);

  /**
   * Post-login actions (roles, audit, etc.)
   */
  const postLogin = useCallback(async () => {
    // Placeholder for future backend enrichment
    dispatch(setRoles([]));
  }, [dispatch]);

  /**
   * Sync MSAL account to Redux once authenticated
   */
  useEffect(() => {
    if (!isAuthenticated || !msalAccount || account?.id) return;

    instance.setActiveAccount(msalAccount);

    const acc = mapMsalAccountToAccount(msalAccount);

    dispatch(setAccount(acc));
    dispatch(setStatus("signedIn"));
  }, [isAuthenticated, msalAccount, account?.id, dispatch]);

  /**
   * Login
   */
  const login = useCallback(
    async (username?: string) => {
      dispatch(setStatus("pending"));

      try {
        const loginRequest = getLoginRequest();

        await instance.loginRedirect({
          ...(loginRequest ?? {}),
          ...(username ? { loginHint: username.trim() } : {}),
          prompt: "select_account",
        });
      } catch (error) {
        console.error("[useAuth] Login failed:", error);
        dispatch(setStatus("signedOut"));
        throw error;
      }
    },
    [dispatch, instance, postLogin]
  );

  /**
   * MFA verification (token re-acquisition)
   */
  const verifyMfa = useCallback(async () => {
    try {
      const loginRequest = getLoginRequest();
      const request = {
        ...loginRequest,
        account: instance.getActiveAccount() ?? undefined,
      };

      try {
        await instance.acquireTokenSilent(request);
      } catch {
        await instance.acquireTokenPopup(request);
      }

      dispatch(setStatus("signedIn"));
    } catch {
      dispatch(setStatus("signedOut"));
      throw new Error("MFA verification failed");
    }
  }, [dispatch, instance]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    localStorage.setItem("msal-logout", Date.now().toString());
    dispatch(setAccount(null));
    dispatch(setRoles([]));
    dispatch(setStatus("signedOut"));

    try {
      let account = instance.getActiveAccount();
      console.warn("#1Logging out account:", account);

      await postLogoutUser();
      //fallback if active account is missing
      if (!account) {
        const accounts = instance.getAllAccounts();
        account = accounts[0]; // pick first account
        console.warn("#2Fallback to account:", account);
      }

      if (!account) {
        console.warn("#3No account found");
        //no account at all → fallback logout
        await instance.logoutRedirect({
          postLogoutRedirectUri: `${window.location.origin}/login`,
        });
        return;
      }

      console.warn("#4Logging out with account:", account);
      //logout with account (prevents picker)
      await instance.logoutRedirect({
        account,
        logoutHint: account.username,
        postLogoutRedirectUri: `${window.location.origin}/login`,
        // onRedirectNavigate: () => {
        //   //Hard redirect after logout
        //   window.location.href = "https://login.microsoftonline.com/logout.srf";
        //   return false;
        // },
      });
    } catch (e) {
      console.error("Logout failed:", e);
    }
  }, [dispatch, instance]);

  return {
    status,
    account,
    roles,
    isAuthenticated,
    login,
    verifyMfa,
    logout,
    postLogin,
  };
};
